import { z } from 'zod';
import {
  CheckRolePermissionCheck,
  CheckRolePermissionsResponse,
  CompletePermission,
  DeleteUserRolesResponse,
  GetPermissionsResponse,
  GetRolePermissionsResponse,
  GetRolesResponse,
  Role,
  RoleEntity,
  RolePermission,
  RoleSubject,
  RoleSubjectEntity,
  RoleVisibility,
  UpdateRolePermissionsResponse,
  UpdatedRole,
} from './model';
import yn from 'yn';

export const RolesSubjectsFiltersSchema = z
  .object({
    subject: z.string(),
    roleId: z.string(),
    entityRef: z.string().optional().nullable(),
    enabled: z.enum(['true', 'false']).default('false'),
  })
  .partial();

export type RolesSubjectsFilters = z.infer<typeof RolesSubjectsFiltersSchema>;

export type DeleteRolesSubjectsParams = {
  filters?: RolesSubjectsFilters;
  options?: { token?: string };
};

export type GetRolesSubjectsParams = {
  searchKeyword?: string;
  filters?: RolesSubjectsFilters;
  options?: { token?: string };
};

export type SyncUserScopesData = {
  users: string[];
  roleId: string;
  scopesToAdd?: string[];
  scopesToRemove?: string[];
};

export type SyncUserScopesParams = {
  syncData: SyncUserScopesData;
  options?: {
    upsert?: boolean;
    token?: string;
  };
};

export type SyncUserScopesResult = {
  added: RoleSubjectEntity[];
  removed: RoleSubjectEntity[];
  errors: string[];
};
export type GetRolesParams = {
  searchKeyword?: string;
  options?: {
    visibility?: RoleVisibility;
    offset?: number;
    limit?: number;
    token?: string;
  };
};

export type GetRolePermissionsParams = {
  roleId: string;
  searchKeyword?: string;
  options?: {
    visibility?: RoleVisibility;
    offset?: number;
    limit?: number;
    token?: string;
  };
};

export type GetRoleParams = {
  roleId?: string;
  options?: {
    token?: string;
  };
};

export type GetUserPermissionsParams = {
  options?: {
    token: string;
    permissionId?: string;
  };
};

export type GetPlatformPermissionsParams = {
  searchKeyword?: string;
  options?: {
    token?: string;
    permissionId?: string;
    offset?: number;
    limit?: number;
  };
};

export type AddRolesSubjectsParams = {
  rolesSubjects: RoleSubject[];
  options?: { upsert?: boolean; token?: string; xUserRef?: string };
};

export type AddRolesParams = {
  roles: Role[];
  options?: { upsert?: boolean; token?: string };
};

export type EditRoleParams = {
  role: UpdatedRole;
  options?: { token?: string };
};

export type DeleteRolesParams = {
  roles: string[];
  options?: { token?: string };
};

export type AddRolesPermissionsParams = {
  rolesPermissions: RolePermission[];
  options?: { upsert?: boolean; token?: string };
};

export type UpdateRolesPermissionsParams = {
  roleId: string;
  permissions: string[];
  options?: { token?: string };
};

export const RolesPermissionsFiltersSchema = z
  .object({
    roleId: z.string(),
    permissionId: z.string(),
  })
  .partial();

export type RolesPermissionsFilters = z.infer<
  typeof RolesPermissionsFiltersSchema
>;

export type DeleteRolePermissionsParams = {
  filters?: RolesPermissionsFilters;
  options?: { token?: string };
};

export type CheckRolePermissionsParams = {
  checks: CheckRolePermissionCheck[];
  options?: { token?: string };
};

export type SubjectScopeInfo = {
  scope: string;
};

export type SubjectScopes = {
  // Scopes for each role assigned to a subject
  roleScopes: Record<string, SubjectScopeInfo[]>;
};

export type GetSubjectsWithPermissionResponse = {
  // Map of subjects to their scopes
  subjects: Record<string, SubjectScopes>;
  total: number;
};

export const GetSubjectsWithPermissionOptionsZod = z.object({
  permissionId: z.string().min(1),
  scope: z.string().min(1),
  excludeWildcard: z
    .preprocess(val => yn(val), z.boolean())
    .optional()
    .default(false),
  roleIds: z
    .preprocess(
      val => (typeof val === 'string' ? val.split(',') : val),
      z.array(z.string()),
    )
    .optional(),
  subjectKind: z.enum(['user', 'group']).optional(),
});

export type GetSubjectsWithPermissionOptions = z.infer<
  typeof GetSubjectsWithPermissionOptionsZod
>;

export type GetSubjectsWithPermissionParams =
  GetSubjectsWithPermissionOptions & {
    options?: { token?: string };
  };

export type SubjectEntry = {
  subject: string;
  roleId: string;
  scopes: string[];
  originGroup: string | null;
};

export type GetSubjectsResponse = {
  subjects: SubjectEntry[];
  total: number;
};

export type GetSubjectsParams = {
  roleIds?: string[];
  scopes?: string[];
  resolveGroups?: boolean;
  search?: string;
  offset?: number;
  limit?: number;
  options?: { token?: string };
};

export type CloneRoleParams = {
  roleId: string;
  displayName: string;
  description?: string;
  cloneAssignments?: boolean;
  options?: { token?: string };
};

/**
 * RBAC Api definition for frontend clients
 */
export interface RbacApi {
  /**
   * Deletes roles subjects rows according to defined filters
   * @param options
   */
  deleteRolesSubjects(
    params: DeleteRolesSubjectsParams,
  ): Promise<Array<RoleSubjectEntity>>;

  getRolesSubjects(
    params: GetRolesSubjectsParams,
  ): Promise<Array<RoleSubjectEntity>>;

  syncUserScopes(params: SyncUserScopesParams): Promise<SyncUserScopesResult>;

  getAvailableEditors(params: {
    options?:
      | {
          token?: string | undefined;
        }
      | undefined;
  }): Promise<number>;

  getRoles(params: GetRolesParams): Promise<GetRolesResponse>;

  getRole(params: GetRoleParams): Promise<RoleEntity>;

  getRolePermissions(
    params: GetRolePermissionsParams,
  ): Promise<GetRolePermissionsResponse>;

  getUserPermissions(
    params: GetUserPermissionsParams,
  ): Promise<Array<CompletePermission>>;

  getPlatformPermissions(
    params: GetPlatformPermissionsParams,
  ): Promise<GetPermissionsResponse>;

  addRolesSubjects(
    params: AddRolesSubjectsParams,
  ): Promise<Array<RoleSubjectEntity>>;

  addRoles(params: AddRolesParams): Promise<Array<RoleEntity>>;

  editRole(params: EditRoleParams): Promise<RoleEntity>;

  deleteUserRoles(params: DeleteRolesParams): Promise<DeleteUserRolesResponse>;

  addRolesPermissions(
    params: AddRolesPermissionsParams,
  ): Promise<Array<RolePermission>>;

  deleteRolePermissions(
    params: DeleteRolePermissionsParams,
  ): Promise<Array<RolePermission>>;

  updateRolePermissions(
    params: UpdateRolesPermissionsParams,
  ): Promise<UpdateRolePermissionsResponse>;

  checkRolePermissions(
    params: CheckRolePermissionsParams,
  ): Promise<CheckRolePermissionsResponse>;

  getSubjectsWithPermission(
    params: GetSubjectsWithPermissionParams,
  ): Promise<GetSubjectsWithPermissionResponse>;

  getSubjects(params: GetSubjectsParams): Promise<GetSubjectsResponse>;

  cloneRole(params: CloneRoleParams): Promise<RoleEntity>;
}
